import * as Icons from "@components/atoms/Icons"
import { cloneElement, isValidElement, ReactElement, ReactNode, useRef, useState } from "react"
import { useInView } from "motion/react"
import { useIsMobile } from "@hooks/useViewportDerived"
import { twMerge } from "tailwind-merge"
import { Link } from "eddev/routing"

export type ToolCardProps = {
  title?: string | null
  description: string | null
  toolLink?: {
    target?: string | null
    title?: string | null
    url?: string | null
  } | null
  latestReportLink?: {
    target?: string | null
    title?: string | null
    url?: string | null
  } | null
  animation: ReactNode
  comingSoon?: boolean | null
  large?: boolean
}

export const ToolCard = ({
  title,
  description,
  toolLink,
  latestReportLink,
  animation,
  comingSoon,
  large,
}: ToolCardProps) => {
  const [infoOpen, setInfoOpen] = useState<boolean>(false)
  const [mouseHovered, setMouseHovered] = useState<boolean>(false)
  const isMobile = useIsMobile()
  const cardRef = useRef<HTMLDivElement>(null)

  const inView = useInView(cardRef, { amount: 1 })
  const hovered = isMobile ? inView : mouseHovered

  const animationWithHover = isValidElement(animation)
    ? cloneElement(animation as ReactElement<{ hover?: boolean }>, { hover: hovered })
    : animation

  if (large)
    return (
      <div
        ref={cardRef}
        onMouseEnter={() => setMouseHovered(true)}
        onMouseLeave={() => setMouseHovered(false)}
        className="w-full grid grid-cols-12 p-6 bg-black text-white rounded-md"
      >
        <div className="col-span-12 md:col-span-7 flex flex-col gap-6 justify-between h-full">
          <div className="flex flex-col gap-4">
            <span className="type-label-caps">Interactive Tool</span>
            <h3>{title}</h3>
            <p className="type-body-m">{description}</p>
          </div>
          <div className="col-span-12 md:col-span-5 flex md:hidden items-center justify-center">
            {animationWithHover}
          </div>
          <div className="flex gap-4 w-full">
            {comingSoon && (
              <div className="h-[40px] type-label-m hover:cursor-not-allowed flex gap-2 items-center justify-between border border-solid border-dark-grey bg-dark-grey text-black rounded-md px-4">
                Coming Soon
              </div>
            )}
            {toolLink?.url && !comingSoon && (
              <Link
                href={toolLink.url}
                target={toolLink.target ? "_blank" : undefined}
                className="h-[40px] flex gap-2 items-center justify-between border border-solid border-white rounded-md px-4 hover:bg-yellow hover:border-yellow hover:text-black aaa-animate"
              >
                <span className="type-label-m">{toolLink.title}</span>
                {Icons["ARROW_RIGHT"]}
              </Link>
            )}

            {latestReportLink?.url && (
              <Link
                href={latestReportLink.url}
                target={latestReportLink.target ? "_blank" : undefined}
                className="h-[40px] flex gap-2 items-center justify-center border border-solid border-white rounded-md px-4 hover:bg-yellow hover:border-yellow hover:text-black aaa-animate"
              >
                <span className="type-label-m">{latestReportLink.title}</span>
              </Link>
            )}
          </div>
        </div>
        <div className="col-span-12 md:col-span-5 hidden md:flex items-center justify-center">{animationWithHover}</div>
      </div>
    )

  return (
    <div
      ref={cardRef}
      onMouseEnter={() => setMouseHovered(true)}
      onMouseLeave={() => setMouseHovered(false)}
      className="w-full h-full flex justify-between flex-col gap-2 p-5 bg-black text-white rounded-md relative overflow-hidden"
    >
      <div className="flex gap-2 items-start justify-between w-full">
        <div className="flex flex-col gap-2 relative">
          <span className="type-label-caps">Interactive Tool</span>
          <h3>{title}</h3>
        </div>
        <button
          className="min-w-[32px] h-[32px] flex items-center justify-center border border-solid border-white rounded-sm hover:bg-white hover:text-black aaa-animate"
          onClick={() => setInfoOpen(!infoOpen)}
        >
          {infoOpen ? <>{Icons.CLOSE}</> : <>{Icons.INFO}</>}
        </button>
      </div>

      <div className="[&_svg]:w-full [&_svg]:h-auto">{animationWithHover}</div>

      <div className="flex gap-2">
        {comingSoon && (
          <div className="w-full  h-[40px] type-label-m hover:cursor-not-allowed flex gap-2 items-center justify-center border border-solid border-dark-grey bg-dark-grey rounded-md px-4">
            Coming Soon
          </div>
        )}

        {toolLink?.url && !comingSoon && (
          <Link
            href={toolLink.url}
            target={toolLink.target ? "_blank" : undefined}
            className="h-[40px] flex w-full gap-2 items-center justify-between border border-solid border-white rounded-md px-4 hover:bg-yellow hover:border-yellow hover:text-black aaa-animate"
          >
            <span className="type-label-m">{toolLink.title}</span>
            {Icons["ARROW_RIGHT"]}
          </Link>
        )}

        {latestReportLink?.url && (
          <Link
            href={latestReportLink.url}
            target={latestReportLink.target ? "_blank" : undefined}
            className="h-[40px] flex w-full gap-2 items-center justify-center border border-solid border-white rounded-md px-4 hover:bg-yellow hover:border-yellow hover:text-black aaa-animate"
          >
            <span className="type-label-m">{latestReportLink.title}</span>
          </Link>
        )}
      </div>

      <div
        className={twMerge(
          "w-full h-auto bg-black/90 p-5 absolute bottom-0 left-0 aaa-animate",
          infoOpen ? "translate-y-0" : "translate-y-full",
        )}
      >
        {description}
      </div>
    </div>
  )
}
